import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiClientInterface } from '../../client/api/api-client.interface';
import {
    IdInterface,
    OptimalSelectedProductInterface,
    OrderClientInterface,
    OrderDocumentInterface,
    OrderSignedInterface,
    OrderTypeAvailableInterface,
    SelectedProductInterface,
} from '../data';
import { Transaction } from '../transaction/transaction';
import { TransactionRequestInterface } from '../transaction/transaction-request.interface';

@Injectable()
export class OrderApiRepository {
    constructor(private apiClient: ApiClientInterface, private transactionRequest: TransactionRequestInterface) {}

    loadOptimal(contractCode: IdInterface, product: SelectedProductInterface): Observable<Transaction<OptimalSelectedProductInterface>> {
        return this.transactionRequest.wrap(
            this.apiClient.getOptimalSelectedProduct(contractCode, product.encryptedCode, product.order.amount)
        );
    }

    loadSignatories(product: SelectedProductInterface, clientCode: IdInterface): Observable<Transaction<OrderClientInterface[]>> {
        return this.transactionRequest.wrap(this.apiClient.getSelectedProductsSignatories(product, clientCode));
    }

    sign(product: SelectedProductInterface): Observable<Transaction<OrderSignedInterface>> {
        return this.transactionRequest.wrap(this.apiClient.addOrderSelectedProduct(product));
    }

    update(product: SelectedProductInterface): Observable<Transaction<OrderSignedInterface>> {
        return this.transactionRequest.wrap(this.apiClient.updateOrderSelectedProduct(product));
    }

    remove(product: SelectedProductInterface): Observable<Transaction<boolean>> {
        return this.transactionRequest.wrap(this.apiClient.removeOrderSelectedProduct(product));
    }

    continue(product: SelectedProductInterface, clientCode: IdInterface): Observable<Transaction<OrderSignedInterface>> {
        return this.transactionRequest.wrap(this.apiClient.continueOrderElectronicSignature(product, clientCode));
    }

    remember(product: SelectedProductInterface, clientCode: IdInterface): Observable<Transaction<boolean>> {
        return this.transactionRequest.wrap(this.apiClient.rememberOrderElectronicSignature(product, clientCode));
    }

    acceptSignatories(product: SelectedProductInterface): Observable<Transaction<void>> {
        return new Observable((observer) => {
            this.transactionRequest.wrap(this.apiClient.acceptOrderSignatures(product.id)).subscribe((transaction) => {
                if (transaction.isSuccess) {
                    observer.next(Transaction.success());
                } else {
                    observer.next(Transaction.error(transaction.error));
                }

                observer.complete();
            });
        });
    }

    documents(product: SelectedProductInterface): Observable<Transaction<any>> {
        return this.transactionRequest.wrap(this.apiClient.getOrderDocuments(product.id));
    }

    signedDocuments(contractCode: IdInterface): Observable<Transaction<OrderDocumentInterface[]>> {
        return this.transactionRequest.wrap(this.apiClient.getContractSignedDocuments(contractCode));
    }

    types(
        contractCode: IdInterface,
        product: SelectedProductInterface,
        clientCode?: IdInterface
    ): Observable<Transaction<OrderTypeAvailableInterface[]>> {
        return this.transactionRequest.wrap(this.apiClient.getOrderTypes(contractCode, product, clientCode));
    }

    upload(product: SelectedProductInterface, document: File): Observable<Transaction<void>> {
        return new Observable((observer) => {
            this.transactionRequest.wrap(this.apiClient.uploadSignedOrderDocument(product.id, document)).subscribe((transaction) => {
                if (transaction.isSuccess) {
                    observer.next(Transaction.success());
                } else {
                    observer.next(Transaction.error(transaction.error));
                }

                observer.complete();
            });
        });
    }
}
